// Onboarding finale: the brick wall (prototype r5, /workspace/ship/wall-transition).
// r3 spring build wave (no logo) -> matter.js "Fall" collapse where the bottom course gives way.
// ~2.1s. Canvas render, one draw per frame. matter.js is lazy-loaded only for this moment.
// Reduced motion = paper fade. Plays once per user (localStorage flag).
const PAPER = '#F4F1EB';
const SHADES = ['#D2542B', '#CB5029', '#C54B25', '#D65B33'];
const PENDING_KEY = 'bricks.onb.finale.pending';
const seenKey = (uid) => `bricks.onb.finale.seen.${uid || 'anon'}`;

export function markFinalePending() { try { sessionStorage.setItem(PENDING_KEY, '1'); } catch {} }
export function finaleSeen(uid) { try { return localStorage.getItem(seenKey(uid)) === '1'; } catch { return true; } }
function markSeen(uid) { try { localStorage.setItem(seenKey(uid), '1'); sessionStorage.removeItem(PENDING_KEY); } catch {} }
export function finalePending(uid) {
  try { return sessionStorage.getItem(PENDING_KEY) === '1' && !finaleSeen(uid); } catch { return false; }
}

let matterP = null;
function loadMatter() {
  if (window.Matter) return Promise.resolve(window.Matter);
  if (!matterP) {
    matterP = new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = '/vendor/matter-0.20.0.min.js';
      s.async = true;
      s.onload = () => res(window.Matter);
      s.onerror = () => { matterP = null; rej(new Error('matter load failed')); };
      document.head.appendChild(s);
    });
  }
  return matterP;
}

function rnd(seed) { let s = seed % 2147483647 || 1; return () => (s = (s * 16807) % 2147483647) / 2147483647; }

// Damped spring step response (zeta 0.72, omega 15) — same curve as the prototype's linear() spring.
function spring(t) {
  const z = 0.72, w = 15, wd = w * Math.sqrt(1 - z * z);
  if (t >= 0.52) return 1;
  return 1 - Math.exp(-z * w * t) * (Math.cos(wd * t) + ((z * w) / wd) * Math.sin(wd * t));
}

// Precomputed rigid-body collapse (verbatim port of prototype collapse.js).
function simCollapse(M, bricks, W, H, BH, seed) {
  const R = rnd(seed), G = 0.0046;
  const eng = M.Engine.create({ gravity: { x: 0, y: 1, scale: G }, positionIterations: 6, velocityIterations: 4, enableSleeping: true });
  const bodies = bricks.map((b) => {
    const o = M.Bodies.rectangle(b.cx, b.cy, b.w - 0.6, BH - 0.6, { friction: 0.5, frictionStatic: 0.8, frictionAir: 0, restitution: 0.06, density: 0.0016, slop: 0.02, chamfer: { radius: 1.5 } });
    o.sleepThreshold = 1e9; return o;
  });
  M.Composite.add(eng.world, bodies); bodies.forEach((b) => M.Sleeping.set(b, true));
  const n = bricks.length;
  const below = bricks.map((a) => {
    const r = [];
    bricks.forEach((c, j) => { if (Math.abs(c.cy - (a.cy + BH)) < 2 && Math.min(a.cx + a.w / 2, c.cx + c.w / 2) - Math.max(a.cx - a.w / 2, c.cx - c.w / 2) > 3) r.push(j); });
    return r;
  });
  const y0 = bodies.map((b) => b.position.y), x0 = bodies.map((b) => b.position.x);
  const awake = new Uint8Array(n), wakeAt = new Float32Array(n).fill(1e9);
  const PX = W * 0.38, bottom = Math.max(...bricks.map((b) => b.cy));
  bricks.forEach((b, i) => { if (b.cy > bottom - 1) { const d = Math.max(0, Math.abs(b.cx - PX) - W * 0.14); wakeAt[i] = Math.pow(d / (W * 0.5), 1.2) * 300 + R() * 10; } });
  const out = [], DT = 1000 / 120, MAXF = 72; let step = 0;
  const wake = (i) => { if (awake[i]) return; awake[i] = 1; M.Sleeping.set(bodies[i], false); const s = Math.sign(bricks[i].cx - PX) || 1; M.Body.setAngularVelocity(bodies[i], (R() - 0.5) * 0.02 + s * 0.006); };
  function run(k) {
    for (let q = 0; q < k && out.length < MAXF; q++) {
      const t = step * DT;
      for (let i = 0; i < n; i++) {
        if (awake[i]) continue;
        if (t >= wakeAt[i]) { wake(i); continue; }
        const B = below[i]; if (!B.length) continue;
        let all = true, vy = 1e9, vx = 0;
        for (const j of B) { if (!awake[j] || bodies[j].position.y - y0[j] < 0.05) { all = false; break; } const v = bodies[j].velocity; if (v.y < vy) { vy = v.y; vx = v.x; } }
        if (all) { wake(i); M.Body.setVelocity(bodies[i], { x: vx * 0.9 + (R() - 0.5) * 0.25, y: vy * (0.95 + R() * 0.05) }); }
      }
      M.Engine.update(eng, DT); step++;
      if (step % 2 === 0) {
        const f = new Float32Array(n * 3); let alive = 0;
        for (let i = 0; i < n; i++) { const b = bodies[i]; f[i * 3] = b.position.x - x0[i]; f[i * 3 + 1] = b.position.y - y0[i]; f[i * 3 + 2] = b.angle; if (b.position.y - BH * 1.6 < H) alive++; }
        out.push(f); if (!alive) { out.done = true; break; }
      }
    }
    return out.length >= MAXF || !!out.done;
  }
  return { frames: out, run };
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

/**
 * Play the wall once. `onCovered` fires when the screen is fully bricked (navigate there).
 * Resolves when the wall has cleared. Safe to call when not pending (no-op).
 */
export async function playWallFinale({ userId, onCovered, startCovered = false, keepPending = false } = {}) {
  if (!keepPending) markSeen(userId);
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const W = window.innerWidth, H = window.innerHeight, dpr = Math.min(2, window.devicePixelRatio || 1);
  const cv = document.createElement('canvas');
  cv.setAttribute('aria-hidden', 'true');
  cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
  cv.style.cssText = `position:fixed;inset:0;width:${W}px;height:${H}px;z-index:2147483000;pointer-events:none;contain:strict`;
  document.body.appendChild(cv);
  const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr);
  const cleanup = () => cv.remove();

  if (reduce) {
    ctx.fillStyle = PAPER; ctx.fillRect(0, 0, W, H);
    if (!startCovered) await cv.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, fill: 'both' }).finished.catch(() => {});
    try { onCovered && onCovered(); } catch {}
    await new Promise((r) => setTimeout(r, 200));
    await cv.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, fill: 'both' }).finished.catch(() => {});
    cleanup(); return;
  }

  const BH = 32, COLS = W < 520 ? 6 : Math.min(10, Math.round(W / 84)), cw = W / COLS, R = rnd(7);
  const T = { build: 640, lay: 500 }, E = 1250;
  const bricks = []; const ROWS = Math.ceil(H / BH);
  for (let r = 0; r < ROWS; r++) {
    const top = H - (r + 1) * BH, off = r % 2 ? cw / 2 : 0, n = COLS + (r % 2 ? 1 : 0);
    for (let c = 0; c < n; c++) {
      const a = Math.max(0, Math.round(c * cw - off)), b = Math.min(W, Math.round((c + 1) * cw - off));
      if (b - a < 4 || top + BH <= 0) continue;
      bricks.push({ x: a, y: top, w: b - a, cx: (a + b) / 2, cy: top + BH / 2, color: SHADES[(r * 7 + c * 3) % 4] });
    }
  }
  for (const b of bricks) {
    const h = (H - b.cy) / H;
    b.delay = Math.max(0, Math.pow(h, 0.92) * T.build + Math.sin((b.cx / W) * Math.PI * 1.3 + h * 2.2) * 40 + R() * 60 - 30);
    b.drop = 18 + R() * 26; b.rot = ((R() - 0.5) * 7 * Math.PI) / 180; b.dur = T.lay + R() * 80;
  }
  const draw = (b, dx, dy, ang, alpha) => {
    if (alpha <= 0) return;
    ctx.globalAlpha = alpha;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(b.cx + dx, b.cy + dy); if (ang) ctx.rotate(ang);
    ctx.fillStyle = PAPER; ctx.fillRect(-b.w / 2, -BH / 2, b.w, BH);
    ctx.fillStyle = b.color; roundRect(ctx, -b.w / 2 + 1, -BH / 2 + 1, b.w - 2, BH - 2, 2); ctx.fill();
  };

  // Lazy-load physics while the build plays; precompute in small chunks between frames.
  let sim = null, simDone = false;
  if (startCovered) {
    // Arriving on the dashboard behind a finished wall: paint it at once, then wait for physics.
    for (const b of bricks) draw(b, 0, 0, 0, 1);
    try { sim = simCollapse(await loadMatter(), bricks, W, H, BH, 11); } catch {}
  }
  if (!sim) loadMatter().then((M) => {
    sim = simCollapse(M, bricks, W, H, BH, 11);
    const chunk = () => { if (!cv.isConnected || simDone) return; simDone = sim.run(2); if (!simDone) setTimeout(chunk, 0); };
    setTimeout(chunk, 0);
  }).catch(() => {});

  await new Promise((resolve) => {
    let t0 = 0, covered = false, fallT0 = 0;
    const frame = (now) => {
      if (!t0) t0 = startCovered ? now - E : now;
      const t = now - t0;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.globalAlpha = 1; ctx.clearRect(0, 0, W, H);
      if (t < E || !covered) {
        for (const b of bricks) {
          const lt = (t - b.delay) / b.dur;
          if (lt <= 0) continue;
          const k = Math.min(1, lt), s = spring(k * 0.52);
          draw(b, 0, -b.drop * (1 - s), b.rot * (1 - s), Math.min(1, k / 0.18));
        }
        if (t >= E) {
          covered = true;
          try { onCovered && onCovered(); } catch {}
          if (sim && !simDone) { while (!sim.run(50)); simDone = true; }
        }
        requestAnimationFrame(frame); return;
      }
      if (!sim) { // physics unavailable: fade the finished wall out
        if (!fallT0) fallT0 = now;
        const a = 1 - Math.min(1, (now - fallT0) / 300);
        for (const b of bricks) draw(b, 0, 0, 0, a);
        if (a > 0) requestAnimationFrame(frame); else resolve();
        return;
      }
      if (!fallT0) fallT0 = now;
      const F = sim.frames, k = Math.min(F.length - 1, Math.floor((now - fallT0) / (1000 / 60))), f = F[k];
      for (let i = 0; i < bricks.length; i++) draw(bricks[i], f[i * 3], f[i * 3 + 1], f[i * 3 + 2], 1);
      if (k < F.length - 1) requestAnimationFrame(frame); else resolve();
    };
    requestAnimationFrame(frame);
  });
  cleanup();
}

const COVERED_KEY = 'bricks.onb.finale.covered';

/** Build the wall over the "You're set up" screen, then hard-navigate behind it. */
export function buildWallThenGo(userId, href) {
  if (finaleSeen(userId)) { window.location.replace(href); return; }
  return playWallFinale({
    userId, keepPending: true,
    onCovered: () => { try { sessionStorage.setItem(COVERED_KEY, '1'); } catch {} window.location.replace(href); },
  });
}

/** Called once on app pages after login. Collapses the wall to reveal the dashboard. */
export function maybeRunFinaleOnArrival(userId) {
  let covered = false;
  try { covered = sessionStorage.getItem(COVERED_KEY) === '1'; sessionStorage.removeItem(COVERED_KEY); } catch {}
  if (covered) return playWallFinale({ userId, startCovered: true });
  const q = new URLSearchParams(window.location.search);
  const paidReturn = q.has('session_id') || q.has('brokerage_session_id');
  if (paidReturn && finalePending(userId)) return playWallFinale({ userId });
  return null;
}
